// Telling a re-cut from a new question, and what follows from it.
//
// The form clears `scores[].feed_name` by design: a new prompt is a new signal and must not claim the
// source's feed. A RE-CUT is the opposite case — the prompt is unchanged, so the question asked of the
// model is identical and only the plumbing around the answer moved. There, breaking the feed is the
// error.
//
// The form does not have to ask which one it is looking at. A copy whose prompt still equals the
// source's is the same signal, whatever else changed; the contract having been edited at all is what
// distinguishes it from a source that was merely picked.

import { continuityFeedName, feedNameChangesOnAdd } from './feedName';

export const isReCut = ({ form, definition, contractDirty }) => {
  if (!form || !definition || !contractDirty) return false;
  const sourcePrompt = (definition.score_gen || {}).prompt || '';
  return String(form.prompt || '') === sourcePrompt;
};

// The feed_name patch that keeps a re-cut publishing where its source published. Applied to the
// score rows, so the payload carries it exactly where the engine reads it.
export const withFeedContinuity = (scoreRows, definition) => {
  const sourceId = definition.score_spec_id;
  const sourceScores = (definition.output_extraction || {}).scores;
  // A legacy source desugars to one score, so an absent list still means N=1 — which is the bare-name
  // case, and the reason this falls back to a one-element list rather than to zero.
  const counted = Array.isArray(sourceScores) ? sourceScores : [{ name: 'score' }];
  return (scoreRows || []).map((row, index) => {
    const sourceScore = counted[index];
    // A score the source did not have is a NEW signal, whatever else this copy is. It takes the
    // derived name rather than claiming a feed that was never its own — and giving two rows the same
    // feed_name would have them publishing over each other.
    if (!sourceScore) return row;
    return {
      ...row,
      feed_name: continuityFeedName(sourceId, counted, { ...sourceScore, name: row.name }) || undefined,
    };
  });
};

// What continuity does and does not cover, in words, for the panel that offers it.
//
// The split trap — at N=1 the feed name is bare, and a second score suffixes it, silently breaking
// every consumer and pin — cannot bite while continuity is on, because the pinned `feed_name` holds
// the old name against the count. It is still worth saying which scores are pinned and which are not.
export const continuityNote = (scoreRows, definition) => {
  const sourceScores = (definition.output_extraction || {}).scores;
  const sourceCount = Array.isArray(sourceScores) ? sourceScores.length : 1;
  const added = Math.max(0, (scoreRows || []).length - sourceCount);
  const pinned = feedNameChangesOnAdd(Array.isArray(sourceScores) ? sourceScores : [{ name: 'score' }])
    ? 'The source published under the bare `score_spec_<id>` name, so the copy pins it with an explicit feed_name — which also holds it against the score count.'
    : 'Each of the source’s scores keeps its own feed name.';
  if (added === 0) return pinned;
  return `${pinned} The ${added} score${added === 1 ? '' : 's'} you added publish${
    added === 1 ? 'es' : ''
  } new feeds of ${added === 1 ? 'its' : 'their'} own — a score the source did not have is a new signal.`;
};
